import { searchSchema } from './search-schema'
import { useEffect, useMemo } from 'react'
import { HOME_PROPERTY_FAQS, buildPropertyFaqSchema } from '@/data/property-faq'
import { getPageSeoImageUrl, getSeoImageDimensions } from './seo-images'

export { DEFAULT_SEO_IMAGE_URL } from './seo-images'

const SITE_URL = 'https://dihaw.co'
const JSON_LD_SCRIPT_ID = 'dihaw-seo-jsonld'
const DEFAULT_KEYWORDS = [
  'Dihaw.co',
  'AI property search Indonesia',
  'jual rumah',
  'sewa rumah',
  'properti baru',
  'rumah dijual',
  'apartemen dijual',
  'tanah dijual',
  'ruko dijual',
  'pasang iklan properti',
  'simulasi KPR',
].join(', ')
const FAVICON_LINK_ICO_ID = 'dihaw-favicon-ico'
const FAVICON_LINK_16_ID = 'dihaw-favicon-16'
const FAVICON_LINK_32_ID = 'dihaw-favicon-32'
const FAVICON_LINK_48_ID = 'dihaw-favicon-48'
const APPLE_TOUCH_ICON_LINK_ID = 'dihaw-apple-touch-icon'

type FaviconSet = {
  ico: string
  icon16: string
  icon32: string
  icon48: string
  appleTouchIcon: string
}

const LANDING_FAVICON_SET: FaviconSet = {
  ico: '/favicon-blue.png',
  icon16: '/icons/blue-16.png',
  icon32: '/icons/blue-32.png',
  icon48: '/icons/blue-48.png',
  appleTouchIcon: '/icons/blue-180.png',
}

type SeoConfig = {
  title: string
  description: string
  path?: string
  robots?: string
  type?: 'website' | 'article'
  imageUrl?: string
  imageAlt?: string
  keywords?: string
  locale?: string
  jsonLd?: unknown
  disabled?: boolean
}

function absoluteUrl(path: string) {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`
  return `${SITE_URL}${normalizedPath}`
}

function upsertMetaTag({
  attr,
  key,
  content,
}: {
  attr: 'name' | 'property'
  key: string
  content: string
}) {
  let meta = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`)
  if (!meta) {
    meta = document.createElement('meta')
    meta.setAttribute(attr, key)
    document.head.appendChild(meta)
  }
  meta.setAttribute('content', content)
}

function upsertCanonicalLink(url: string) {
  let link = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]')
  if (!link) {
    link = document.createElement('link')
    link.setAttribute('rel', 'canonical')
    document.head.appendChild(link)
  }
  link.setAttribute('href', url)
}

function upsertAlternateLink({ hreflang, href }: { hreflang: string; href: string }) {
  let link = document.head.querySelector<HTMLLinkElement>(`link[rel="alternate"][hreflang="${hreflang}"]`)
  if (!link) {
    link = document.createElement('link')
    link.setAttribute('rel', 'alternate')
    link.setAttribute('hreflang', hreflang)
    document.head.appendChild(link)
  }
  link.setAttribute('href', href)
}

function upsertLinkTag({
  id,
  rel,
  href,
  type,
  sizes,
}: {
  id: string
  rel: string
  href: string
  type?: string
  sizes?: string
}) {
  let link = document.head.querySelector<HTMLLinkElement>(`#${id}`)
  if (!link) {
    link = document.createElement('link')
    link.id = id
    document.head.appendChild(link)
  }

  link.setAttribute('rel', rel)
  link.setAttribute('href', href)

  if (type) {
    link.setAttribute('type', type)
  } else {
    link.removeAttribute('type')
  }

  if (sizes) {
    link.setAttribute('sizes', sizes)
  } else {
    link.removeAttribute('sizes')
  }
}

function applyFaviconSet() {
  upsertLinkTag({
    id: FAVICON_LINK_ICO_ID,
    rel: 'icon',
    href: LANDING_FAVICON_SET.ico,
    type: LANDING_FAVICON_SET.ico.toLowerCase().includes('.png') ? 'image/png' : 'image/x-icon',
  })
  upsertLinkTag({
    id: FAVICON_LINK_16_ID,
    rel: 'icon',
    href: LANDING_FAVICON_SET.icon16,
    type: 'image/png',
    sizes: '16x16',
  })
  upsertLinkTag({
    id: FAVICON_LINK_32_ID,
    rel: 'icon',
    href: LANDING_FAVICON_SET.icon32,
    type: 'image/png',
    sizes: '32x32',
  })
  upsertLinkTag({
    id: FAVICON_LINK_48_ID,
    rel: 'icon',
    href: LANDING_FAVICON_SET.icon48,
    type: 'image/png',
    sizes: '48x48',
  })
  upsertLinkTag({
    id: APPLE_TOUCH_ICON_LINK_ID,
    rel: 'apple-touch-icon',
    href: LANDING_FAVICON_SET.appleTouchIcon,
    sizes: '180x180',
  })
}

function upsertJsonLdScript(jsonLd?: unknown) {
  const currentScript = document.getElementById(JSON_LD_SCRIPT_ID)
  if (!jsonLd) {
    currentScript?.remove()
    return
  }

  let scriptTag: HTMLScriptElement
  if (currentScript) {
    scriptTag = currentScript as HTMLScriptElement
  } else {
    scriptTag = document.createElement('script')
    scriptTag.id = JSON_LD_SCRIPT_ID
    scriptTag.type = 'application/ld+json'
    document.head.appendChild(scriptTag)
  }
  scriptTag.textContent = JSON.stringify(jsonLd)
}

export function applySeo(config: SeoConfig) {
  const path = config.path ?? window.location.pathname
  const canonicalUrl = absoluteUrl(path)
  const imageUrl = config.imageUrl || getPageSeoImageUrl(path)
  const imageAlt = config.imageAlt ?? 'Dihaw.co property listing preview'
  const keywords = config.keywords ?? DEFAULT_KEYWORDS
  const type = config.type ?? 'website'
  const robots = config.robots ?? 'index, follow'
  const locale = config.locale ?? 'id_ID'

  document.title = config.title
  document.documentElement.lang = locale.toLowerCase().startsWith('id') ? 'id' : 'en'
  applyFaviconSet()
  upsertCanonicalLink(canonicalUrl)
  upsertAlternateLink({ hreflang: 'id-ID', href: canonicalUrl })
  upsertAlternateLink({ hreflang: 'x-default', href: canonicalUrl })

  upsertMetaTag({ attr: 'name', key: 'description', content: config.description })
  upsertMetaTag({ attr: 'name', key: 'keywords', content: keywords })
  upsertMetaTag({ attr: 'name', key: 'robots', content: robots })
  upsertMetaTag({ attr: 'name', key: 'googlebot', content: robots })
  upsertMetaTag({ attr: 'name', key: 'bingbot', content: robots })
  upsertMetaTag({ attr: 'name', key: 'author', content: 'Dihaw.co' })
  upsertMetaTag({ attr: 'name', key: 'application-name', content: 'Dihaw.co' })
  upsertMetaTag({ attr: 'name', key: 'apple-mobile-web-app-title', content: 'Dihaw.co' })
  upsertMetaTag({ attr: 'name', key: 'publisher', content: 'Dihaw.co' })
  upsertMetaTag({ attr: 'name', key: 'coverage', content: 'Indonesia' })
  upsertMetaTag({ attr: 'name', key: 'distribution', content: 'global' })
  upsertMetaTag({ attr: 'name', key: 'referrer', content: 'strict-origin-when-cross-origin' })
  upsertMetaTag({ attr: 'name', key: 'twitter:card', content: 'summary_large_image' })
  upsertMetaTag({ attr: 'name', key: 'twitter:title', content: config.title })
  upsertMetaTag({ attr: 'name', key: 'twitter:description', content: config.description })
  upsertMetaTag({ attr: 'name', key: 'twitter:image', content: imageUrl })
  upsertMetaTag({ attr: 'name', key: 'twitter:image:alt', content: imageAlt })
  upsertMetaTag({ attr: 'name', key: 'theme-color', content: '#edf4ff' })

  upsertMetaTag({ attr: 'property', key: 'og:title', content: config.title })
  upsertMetaTag({ attr: 'property', key: 'og:description', content: config.description })
  upsertMetaTag({ attr: 'property', key: 'og:type', content: type })
  upsertMetaTag({ attr: 'property', key: 'og:url', content: canonicalUrl })
  upsertMetaTag({ attr: 'property', key: 'og:site_name', content: 'Dihaw.co' })
  upsertMetaTag({ attr: 'property', key: 'og:locale', content: locale })
  upsertMetaTag({ attr: 'property', key: 'og:image', content: imageUrl })
  upsertMetaTag({ attr: 'property', key: 'og:image:alt', content: imageAlt })
  const dimensions = getSeoImageDimensions(imageUrl)
  for (const dimension of ['width', 'height'] as const) {
    if (dimensions) {
      upsertMetaTag({ attr: 'property', key: `og:image:${dimension}`, content: String(dimensions[dimension]) })
    } else {
      document.head.querySelector(`meta[property="og:image:${dimension}"]`)?.remove()
    }
  }

  upsertJsonLdScript(searchSchema(canonicalUrl, config.title, config.description, robots, config.jsonLd))
}

export function usePageSeo({
  title,
  description,
  path,
  robots,
  type,
  imageUrl,
  imageAlt,
  keywords,
  locale,
  jsonLd,
  disabled,
}: SeoConfig) {
  const jsonLdString = useMemo(
    () => JSON.stringify(jsonLd ?? null),
    [jsonLd],
  )

  useEffect(() => {
    if (disabled) return
    applySeo({
      title,
      description,
      path,
      robots,
      type,
      imageUrl,
      imageAlt,
      keywords,
      locale,
      jsonLd: jsonLdString ? JSON.parse(jsonLdString) : undefined,
    })
  }, [
    description,
    disabled,
    imageUrl,
    imageAlt,
    jsonLdString,
    keywords,
    locale,
    path,
    robots,
    title,
    type,
  ])
}

export const seoSchemas = {
  home: {
    '@context': 'https://schema.org',
    '@graph': [
      buildPropertyFaqSchema(HOME_PROPERTY_FAQS, '/'),
      {
        '@type': 'Organization',
        name: 'Dihaw.co',
        url: SITE_URL,
        logo: `${SITE_URL}/favicon-blue.png`,
        contactPoint: [
          {
            '@type': 'ContactPoint',
            contactType: 'customer support',
            email: 'hello@dihaw.ai',
            availableLanguage: ['Indonesian', 'English'],
          },
        ],
      },
      {
        '@type': 'WebSite',
        name: 'Dihaw.co',
        url: SITE_URL,
        inLanguage: ['en', 'id'],
        description: 'Platform properti berbantu AI untuk mencari dan memasang listing rumah, apartemen, tanah, ruko, vila, dan gudang di Indonesia.',
        potentialAction: {
          '@type': 'SearchAction',
          target: `${SITE_URL}/?q={search_term_string}`,
          'query-input': 'required name=search_term_string',
        },
      },
      {
        '@type': 'Service',
        name: 'Dihaw.co Listing Properti',
        serviceType: 'AI property search and listing platform',
        areaServed: 'Indonesia',
        url: SITE_URL,
      },
    ],
  },
  privacyPolicy: {
    '@context': 'https://schema.org',
    '@type': 'PrivacyPolicy',
    name: 'Kebijakan Privasi | Dihaw.co',
    url: `${SITE_URL}/privacy-policy`,
    isPartOf: {
      '@type': 'WebSite',
      name: 'Dihaw.co',
      url: SITE_URL,
    },
    publisher: {
      '@type': 'Organization',
      name: 'Dihaw.co',
      url: SITE_URL,
      email: 'hello@dihaw.ai',
    },
  },
  termsOfUse: {
    '@context': 'https://schema.org',
    '@type': 'TermsOfService',
    name: 'Syarat Penggunaan | Dihaw.co',
    url: `${SITE_URL}/terms-of-use`,
    isPartOf: {
      '@type': 'WebSite',
      name: 'Dihaw.co',
      url: SITE_URL,
    },
    publisher: {
      '@type': 'Organization',
      name: 'Dihaw.co',
      url: SITE_URL,
      email: 'hello@dihaw.ai',
    },
  },
}
